import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import LeLiveHeading from './LeLiveHeading'
import LeLiveGetTicketsButton from './LeLiveGetTicketsButton'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'

interface Partner {
  id: string
  name: string
  logoId: string
  logoHeight: number
  mobileLogoHeight?: number
  format?: 'svg' | 'png'
}

const MAJOR_PARTNERS: Array<Partner> = [
  { id: 'mastercard', name: 'Mastercard', logoId: '9sz94m8h9agkc5dm9jp', logoHeight: 88 },
  { id: 'cathay', name: 'Cathay Pacific', logoId: 'aq931u2xbi6gpmlfwu6', logoHeight: 32 },
  { id: 'passportcard', name: 'PassportCard', logoId: 'jnlxnrbgk10uiyhryeus', logoHeight: 88, mobileLogoHeight: 52 },
  { id: 'hktb', name: 'Hong Kong Tourism Board', logoId: 'o54w6ee3jp1h7kgbsak', logoHeight: 88 },
  { id: 'lavazza', name: 'Lavazza', logoId: 'y29wimf6ge2vkc2edudb', logoHeight: 48 },
  { id: 'wine-selectors', name: 'Wine Selectors', logoId: 'zpltmmy3zq0vwwrturd', logoHeight: 36 },
]

const EVENT_PARTNERS: Array<Partner> = [
  { id: 'ep-apurva', name: 'Apurva', logoId: 'tnwlt5opmdxd1ob60ev', logoHeight: 100, mobileLogoHeight: 60 },
  { id: 'ep-centara-anda-dhevi', name: 'Centara Anda Dhevi', logoId: 'ibqg1ejyecm3rjeej5fo', logoHeight: 80 },
  { id: 'ep-australian-bespoke', name: 'Australian Bespoke', logoId: 'v0n11ga9dftfb90yumr', logoHeight: 60 },
  { id: 'ep-celebrity-cruises', name: 'Celebrity Cruises', logoId: 'hvqw9rif4asr9ikpk3yc', logoHeight: 36 },
  { id: 'ep-centara-beach-resort', name: 'Centara Beach Resort', logoId: '4epzjtq3754u918pkg', logoHeight: 84 },
  { id: 'ep-como', name: 'COMO Hotels and Resorts', logoId: 'lcm9h7v6hblljgv1l8w4', logoHeight: 60 },
  { id: 'ep-ayodya', name: 'Ayodya', logoId: 'jt4xhl125g6qptyhjnzu', logoHeight: 60, format: 'svg' },
  { id: 'ep-centara-hotel-osaka', name: 'Centara Hotel Osaka', logoId: 'uvyo4mtyeis1tiu4f3ch', logoHeight: 84 },
  { id: 'ep-dubai-tourism', name: 'Dubai Tourism', logoId: 'rfx57lxnfir87hxf4y7', logoHeight: 90 },
  { id: 'ep-banyan-group', name: 'Banyan Group', logoId: 'r2sq5xseprcdxpd0lkh', logoHeight: 36 },
  { id: 'ep-centara-lagoon-maldives', name: 'Centara Lagoon Maldives', logoId: 'ru2jejxq5r2zedoqe2in', logoHeight: 84 },
  { id: 'ep-ennismore', name: 'Ennismore', logoId: 'trjrz60bjhfofj4hf9m', logoHeight: 48 },
  { id: 'ep-double-six', name: 'Double Six', logoId: 'qkvu353wr41vv7osdiwp', logoHeight: 72 },
  { id: 'ep-centara-karon-villas', name: 'Centara Karon Villas', logoId: 'v5fnhli2qifxlu9t1cb', logoHeight: 36 },
  { id: 'ep-ini-vie-group', name: 'Ini Vie Group', logoId: 't3ek3v0to9rxzx4620n', logoHeight: 84, mobileLogoHeight: 60 },
  { id: 'ep-cook-islands', name: 'Cook Islands', logoId: 'j8cc9c14ss9r973e02', logoHeight: 36 },
  { id: 'ep-centara-mirage-lagoon-maldives', name: 'Centara Mirage Lagoon Maldives', logoId: 'u6n93s3jqg04jcs4763h', logoHeight: 60 },
  { id: 'ep-miral', name: 'Miral', logoId: 'u6bvot5oy8tlbzmnbhl', logoHeight: 48 },
  { id: 'ep-princess-cruises', name: 'Princess Cruises', logoId: 'n1us3p4y4kxgjw5z04ln', logoHeight: 48 },
  { id: 'ep-centara-villas-phi-phi', name: 'Centara Villas Phi Phi', logoId: 'aji3bcxg5cvkqlz2ca3d', logoHeight: 80 },
  { id: 'ep-potique', name: 'Potique Hotels', logoId: 'x096lzv46a9vfaob5zus', logoHeight: 82 },
  { id: 'ep-scenic-emerald', name: 'Scenic - Emerald Cruises and Tours', logoId: 'hbrp2aifgpher5o0ylug', logoHeight: 144 },
  { id: 'ep-centara-villas-samui', name: 'Centara Villas Samui', logoId: '24mwzgyyi9tkmgakz6bej', logoHeight: 80 },
  { id: 'ep-scenic', name: 'Scenic', logoId: '1h4h2hbxgk5tle66v989', logoHeight: 48 },
  { id: 'ep-uniworld', name: 'Uniworld', logoId: 'j9fmishkzju3sy3j8w', logoHeight: 72 },
]

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(12)};
  width: 100%;

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const LogoCard = styled(Sheet)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(73)};
  min-width: ${rem(100)};
  overflow: hidden;

  picture {
    display: flex;
    justify-content: center;
    max-width: none;
  }

  img {
    max-width: 100%;
    object-fit: contain;
  }

  ${mediaQueryUp.desktop} {
    height: ${rem(138)};
  }
`

const LogoImageWrapper = styled.div<{ $mobileMaxHeight?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.$mobileMaxHeight && `
    ${mediaQueryDown.tablet} {
      img {
        max-height: ${rem(props.$mobileMaxHeight)};
        width: auto;
      }
    }
  `}
`

const marqueeScroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`

const MarqueeTrack = styled.div`
  overflow: hidden;
  width: 100%;
`

const SECONDS_PER_ITEM = 2

const MarqueeInner = styled.div<{ $itemCount: number }>`
  display: flex;
  gap: ${rem(12)};
  width: max-content;
  animation: ${marqueeScroll} ${props => props.$itemCount * SECONDS_PER_ITEM}s linear infinite;

  &:hover {
    animation-play-state: paused;
  }

  ${LogoCard} {
    width: ${rem(150)};
    flex-shrink: 0;

    ${mediaQueryUp.desktop} {
      width: ${rem(200)};
    }
  }
`

interface Props {
  hasRegistration: boolean
}

function LeLivePartners({ hasRegistration }: Props) {
  const intl = useIntl()

  return (
    <Sheet padding="56 20" desktopPadding="64 0" id="partners">
      <LayoutContainer>
        <Group direction="vertical" gap={40}>
          <Group direction="vertical" gap={8}>
            <LeLiveHeading variant="heading1alt" as="h2" align="center">
              <FormattedMessage
                id="qfScGL2DySI0NR7jHjJs"
                defaultMessage="Meet our <em>partners</em>"
                description="Main heading for the partners section on the LE Live event page, with emphasis styling on the word partners"
                values={{
                  em: (chunks) => <em>{chunks}</em>,
                }}
              />
            </LeLiveHeading>
            <BodyText variant="large" colour="neutral-two" align="center">
              {intl.formatMessage({
                id: 'VHGUOGKbDmF9TTekuwTR',
                defaultMessage: 'Explore offerings from the world\u2019s most trusted brands, all under one roof.',
                description: 'Partners section subtitle describing partner offerings',
              })}
            </BodyText>
          </Group>

          <Group direction="vertical" gap={40}>
            <Group direction="vertical" gap={12}>
              <BodyText variant="large" colour="neutral-two" align="center">
                {intl.formatMessage({
                  id: 'UaZzYkwAe2ut-g3sJIlh',
                  defaultMessage: 'Major Partners',
                  description: 'Section label above the major partners logo grid on the LE Live event landing page',
                })}
              </BodyText>
              <LogoGrid>
                {MAJOR_PARTNERS.map(partner => (
                  <LogoCard key={partner.id} border="neutral-five" borderRadius="M" padding="16 12">
                    <LogoImageWrapper $mobileMaxHeight={partner.mobileLogoHeight}>
                      <Image
                        id={partner.logoId}
                        alt={partner.name}
                        height={partner.logoHeight}
                        dpr={2}
                        enhancedFormats={false}
                      />
                    </LogoImageWrapper>
                  </LogoCard>
                ))}
              </LogoGrid>
            </Group>

            <Group direction="vertical" gap={12}>
              <BodyText variant="large" colour="neutral-two" align="center">
                {intl.formatMessage({
                  id: 'vtyBiC1JL9ygB7PKvsC9',
                  defaultMessage: 'Event Partners',
                  description: 'Section label above the scrolling event partners marquee on the LE Live event landing page',
                })}
              </BodyText>
              <MarqueeTrack>
                <MarqueeInner $itemCount={EVENT_PARTNERS.length}>
                  {EVENT_PARTNERS.map(partner => (
                    <LogoCard key={partner.id} border="neutral-five" borderRadius="M" padding="16 12">
                      <LogoImageWrapper $mobileMaxHeight={partner.mobileLogoHeight}>
                        <Image
                          id={partner.logoId}
                          alt={partner.name}
                          height={partner.logoHeight}
                          format={partner.format}
                          dpr={partner.format === 'svg' ? undefined : 2}
                          enhancedFormats={partner.format !== 'svg' ? false : undefined}
                        />
                      </LogoImageWrapper>
                    </LogoCard>
                  ))}
                  {EVENT_PARTNERS.map(partner => (
                    <LogoCard key={`${partner.id}-dup`} border="neutral-five" borderRadius="M" padding="16 12" aria-hidden>
                      <LogoImageWrapper $mobileMaxHeight={partner.mobileLogoHeight}>
                        <Image
                          id={partner.logoId}
                          alt={partner.name}
                          height={partner.logoHeight}
                          format={partner.format}
                          dpr={partner.format === 'svg' ? undefined : 2}
                          enhancedFormats={partner.format !== 'svg' ? false : undefined}
                        />
                      </LogoImageWrapper>
                    </LogoCard>
                  ))}
                </MarqueeInner>
              </MarqueeTrack>
            </Group>
          </Group>

          <Group direction="horizontal" horizontalAlign="center">
            <LeLiveGetTicketsButton hidden={hasRegistration} />
          </Group>
        </Group>
      </LayoutContainer>
    </Sheet>
  )
}

export default LeLivePartners
